/** Plain-language Models screens (ADR-0063). Technical names stay in `models` for advanced fields. */
export default {
  en: {
    subtitle: "Connect model services, choose which models agents use, and decide what happens when one is busy or unavailable.",
    online: {
      title: "Before an online model can answer",
      address: {
        ok: "The platform may reach {{host}}.",
        no: "The platform may not reach {{host}} yet: it is not on the list of internet addresses.",
        unknown: "Whether the platform may reach {{host}} is set by the platform team.",
        action: "Add it under Administration › Platform settings",
      },
      organisation: {
        ok: "Your organisation allows online models for this connection.",
        no: "Your organisation doesn't allow online models for this connection yet.",
        unknown: "Whether your organisation allows online models is set by its administrator.",
        action: "Change it under Administration › Organisation",
      },
      level: {
        ok: "Requests up to {{level}} may go online; anything more sensitive always stays on local models.",
        unknown: "Your organisation administrator sets the highest data level that may go online.",
      },
      levels: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
      who: { platform: "the platform team", organisation: "your organisation administrator" },
      ask: "Ask {{who}}.",
    },
    tab: {
      providers: "Connections",
      models: "Models",
      aliases: "Model choices",
      speech: "Speech",
      fallbacks: "Backups",
      routes: "Routing rules",
      budgets: "Spending limits",
      cache: "Saved answers",
    },
    env: { dev: "Development", test: "Testing", staging: "Final check", prod: "Live" },
    advanced: "Advanced settings",
    test: {
      run: "Test connection",
      prompt: "Send a test prompt",
      running: "Testing…",
      ok: "Connected in {{ms}} ms.",
      okModels: "Connected in {{ms}} ms. It offers {{count}} models: {{list}}",
      reply: "The model answered: “{{reply}}” ({{ms}} ms).",
      reason: {
        ok: "Works.",
        egress_blocked: "Blocked: {{host}} is not on the list of internet addresses the platform may reach. Ask the platform admin to allow it (Administration › Internet access), or use a model inside your network.",
        unreachable: "Could not reach the address. Check the address and that the service is running.",
        unauthorised: "The service refused the API key. Check or replace the key.",
        http_error: "The service answered with an error. Check the address.",
        provider_error: "The model did not answer. Check that the model name exists on this service.",
        not_testable: "Only chat and search (embedding) models can be tried here.",
        secret_missing: "The stored API key could not be read. Enter it again.",
      },
    },
    providers: {
      intro: "A connection tells the platform where a model service runs: inside your network (local) or on the internet (online). Online services need an API key and permission to reach the internet.",
      add: "Add a connection",
      name: "Name",
      nameHelp: "A short name, for example “local-gpu” or “openai”.",
      type: "Service",
      where: "Where it runs",
      address: "Address",
      addressHelp: "Filled in for you for most services. Change it only if your service runs elsewhere.",
      apiKey: "API key",
      apiKeyHelp: "Stored safely in the vault. It is never shown again.",
      keyStored: "Stored ✓ · updated {{at}}",
      keyStoredNoDate: "Stored ✓",
      keyNone: "No key",
      keyNotNeeded: "Not needed",
      replaceKey: "Replace key",
      removeKey: "Remove key",
      removeKeyConfirm: "Remove the stored API key? Calls to this service will fail until a new key is entered.",
      keySaved: "API key stored",
      keyRemoved: "API key removed",
      keyPlaceholder: "Paste the key here",
      secretRef: "Existing vault reference (path#key)",
      secretRefHelp: "Only for keys an operator already put in the vault.",
      timeout: "Give up after (seconds)",
      online: "Online — on the internet",
      local: "Local — inside your network",
      saved: "Connection {{name}} saved",
    },
    models: {
      intro: "Models are the AI engines agents use. Start from the catalogue: it fills in the details for well-known models.",
      add: "Add a model",
      fromCatalogue: "Start from the catalogue",
      catalogueHelp: "Pick a known model to fill in its details. You can change anything afterwards.",
      catalogueLocal: "Runs inside your network",
      catalogueOnline: "Online services",
      licence: "Licence: {{licence}}",
      priceNote: "Prices of online services change. Enter the price from your contract.",
      name: "Name agents and admins see",
      connection: "Connection",
      serviceName: "Model name on the service",
      serviceNameHelp: "Exactly as the service calls it, for example “qwen2.5:7b-instruct”.",
      version: "Exact version",
      versionHelp: "Pins the exact weights so results do not change silently.",
      can: "What it can do",
      maxData: "Most sensitive data it may see",
      contextWindow: "How much text it can read at once",
      contextValue: "{{n}} tokens (about {{pages}} pages)",
      maxAnswer: "Longest answer (tokens)",
      priceIn: "Price for reading",
      priceOut: "Price for writing",
      perThousand: "{{currency}} per 1,000 tokens",
      free: "No charge (runs on your own hardware)",
      noPrice: "No price entered yet (an online service: add the price from your contract)",
      readyIn: "Ready for {{env}}",
      about: "About",
      dataUpTo: "Data up to {{level}}",
      speed: { fast: "Fast", medium: "Slower on a laptop", slow: "Needs a GPU server" },
      card: {
        "qwen2-5-0-5b": "Tiny model for quick safety checks and tests. Not for real answers.",
        "qwen2-5-1-5b": "Small private model: fast, but simple answers and unreliable with systems and forms.",
        "qwen3-4b": "Stronger private model: uses systems, fills forms and writes structured answers well, in Arabic and English. Slower than online models.",
        "qwen2-5-7b": "Good everyday private model in Arabic and English. Needs more memory than a laptop stack has.",
        "bge-m3": "Finds matching passages in your documents (search). Does not write answers.",
      },
      caps: {
        chat: "Chat and answers",
        embeddings: "Search (embeddings)",
        rerank: "Re-rank search results",
        asr: "Understands speech",
        tts: "Speaks",
        vision: "Read images",
        ocr: "Read scanned documents",
        tools: "Use tools",
        json_schema: "Fill in forms (structured answers)",
        streaming: "Show answers as they are written",
      },
    },
    aliases: {
      title: "Model choices — the names agents use",
      intro: "Agents ask for a model choice, such as “Standard chat”, not a specific model. Here you decide which models answer and how traffic is shared. Change models here without changing any agent.",
      add: "Add a model choice",
      name: "Name agents use",
      description: "What it is for",
      models: "Models and traffic share",
      share: "Share of requests",
      quiet: "Try quietly (0% traffic, still testable)",
      quietTag: "Trying quietly",
      addModel: "Add a model",
      removeModel: "Remove model {{n}}",
      total: "Total: {{pct}}",
      totalBad: "The shares must add up to 100%.",
      even: "Share evenly",
      saved: "Model choice {{name}} saved",
    },
    fallbacks: {
      intro: "A backup is used automatically when a model does not answer. Both models must be approved for that stage.",
      add: "Add a backup",
      sentence: "If {{primary}} is unavailable in {{env}}, use {{fallback}}.",
      environment: "Where",
      primary: "If this model is unavailable",
      fallback: "use this model instead",
      order: "Order (when there are several backups)",
      orderHelp: "Lower numbers are tried first.",
      first: "Try first",
      saved: "Backup saved",
    },
    routes: {
      intro: "Routing rules redirect requests in special cases, for example sending confidential data only to models inside your network. Rules are checked from the top; the first one that matches is used.",
      add: "Add a routing rule",
      sentence: {
        when: "When",
        anyAgent: "an agent asks for any model choice",
        asks: "an agent asks for {{aliases}}",
        task: "for the task {{tasks}}",
        tenant: "from {{tenants}}",
        data: "with {{cls}} data or more sensitive",
        use: "→ use {{alias}}",
        keep: "→ keep the model choice",
        pick: { weighted: "and share traffic as set", cheapest: "and pick the cheapest", fastest: "and pick the fastest" },
      },
      edit: {
        aliases: "When an agent asks for",
        anyChoice: "Any model choice",
        tasks: "Only for these tasks (optional)",
        tenants: "Only for these organisations (optional)",
        minData: "Only when the data is at least",
        anyData: "Any data",
        routeTo: "Use this model choice",
        sameChoice: "Keep what was asked for",
        strategy: "How to pick the model",
        order: "Check order",
        orderHelp: "Rules with a lower number are checked first.",
      },
      strategy: { weighted: "Share traffic as set", cheapest: "Cheapest", fastest: "Fastest" },
      moveUp: "Move rule {{name}} up",
      moveDown: "Move rule {{name}} down",
      saved: "Routing rule saved",
    },
    sim: {
      title: "Which rule applies?",
      intro: "Try a request and see which rule is used and which model would answer, with the reasons.",
      alias: "Model choice asked for",
      task: "Task (optional)",
      tenant: "Organisation",
      data: "Data sensitivity",
      run: "Show the result",
      ruleUsed: "Rule used: {{rule}}",
      noRule: "No rule matched — the model choice is used as asked.",
      becomes: "Model choice used: {{alias}}",
      answers: "{{model}} answers ({{pct}} of requests)",
      backups: "Backups if it fails: {{list}}",
      noBackups: "No backup model.",
      missed: { alias: "asks for a different model choice", task: "different task", tenant: "different organisation", classification: "data is less sensitive" },
      ruleSkipped: "{{rule}} — skipped: {{why}}",
      targetReason: {
        ok: "can answer",
        missing_capability: "cannot do this kind of request",
        not_approved: "not approved for this stage yet",
        classification_too_high: "not allowed to see this data",
        online_ceiling: "online model — data too sensitive to leave the network",
        egress_blocked: "online model — internet address not allowed",
      },
      outcome: {
        ok: "Matched",
        unknown_alias: "No model choice with this name exists.",
        no_capable_model: "No model behind this choice can do this kind of request.",
        no_approved_model: "No model behind this choice is approved yet.",
        no_fitting_model: "No model behind this choice may receive this data.",
      },
    },
    cache: {
      intro: "Saved answers make repeated questions faster and cheaper by reusing an earlier answer.",
      preset: { off: "Off", safe: "Safe", aggressive: "Aggressive", custom: "Custom" },
      presetHelp: {
        off: "Every question goes to a model. Most up to date, highest cost.",
        safe: "Reuse an answer only for exactly the same question, for 1 hour. Recommended.",
        aggressive: "Also reuse answers to very similar questions, for up to 1 day. Cheapest; answers may be less fresh.",
        custom: "Your own settings (see Advanced settings).",
      },
      hitRate: "Last {{days}} days: {{pct}} of {{requests}} requests were answered from saved answers.",
      hitRateNone: "No requests in the last {{days}} days.",
      keepFor: "Keep saved answers for",
      minutes: "minutes",
      hours: "hours",
      neverAbove: "Never save answers for data above",
      similarity: "How similar a question must be (higher = stricter)",
      embedChoice: "Model choice used to compare questions",
      needsEmbed: "Aggressive needs a local search (embedding) model choice.",
      saved: "Saved answers settings saved",
    },
    limits: {
      title: "Spending limits moved",
      body: "Spending limits are now set in one place: Administration › Spending limits. Finance and organisation admins set limits per organisation, team, project or agent there.",
      open: "Open Spending limits",
      legacyTitle: "Older limits set on this page",
      legacyBody: "These limits were set here before spending limits moved. They are no longer used: the limits in Administration › Spending limits apply instead. You can remove them.",
      legacyActive: "This installation still checks these older limits, because spending is not counted centrally here.",
      tokens: "{{n}} tokens (about {{words}} words)",
      who: "Covers",
      amount: "Limit",
      remove: "Remove limit {{name}}",
      removeConfirm: "Remove this older limit?",
      removed: "Older limit removed",
    },
  },
  ar: {
    subtitle: "اربط خدمات النماذج، واختر النماذج التي يستخدمها الوكلاء، وحدد ما يحدث عندما يكون نموذج مشغولًا أو غير متاح.",
    online: {
      title: "قبل أن يجيب نموذج عبر الإنترنت",
      address: {
        ok: "يمكن للمنصة الوصول إلى {{host}}.",
        no: "لا يمكن للمنصة الوصول إلى {{host}} بعد: العنوان ليس في قائمة عناوين الإنترنت.",
        unknown: "يحدد فريق المنصة ما إذا كان يمكن الوصول إلى {{host}}.",
        action: "أضفه في الإدارة › إعدادات المنصة",
      },
      organisation: {
        ok: "تسمح جهتك بالنماذج عبر الإنترنت لهذا الاتصال.",
        no: "لا تسمح جهتك بعد بالنماذج عبر الإنترنت لهذا الاتصال.",
        unknown: "يحدد مسؤول جهتك ما إذا كانت النماذج عبر الإنترنت مسموحة.",
        action: "غيّره في الإدارة › الجهة",
      },
      level: {
        ok: "يمكن إرسال الطلبات حتى مستوى {{level}} عبر الإنترنت؛ وما هو أكثر حساسية يبقى دائمًا على النماذج المحلية.",
        unknown: "يحدد مسؤول جهتك أعلى مستوى بيانات يمكن إرساله عبر الإنترنت.",
      },
      levels: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيد" },
      who: { platform: "فريق المنصة", organisation: "مسؤول جهتك" },
      ask: "اسأل {{who}}.",
    },
    tab: {
      providers: "الاتصالات",
      models: "النماذج",
      aliases: "خيارات النماذج",
      speech: "الكلام",
      fallbacks: "البدائل",
      routes: "قواعد التوجيه",
      budgets: "حدود الإنفاق",
      cache: "الإجابات المحفوظة",
    },
    env: { dev: "التطوير", test: "الاختبار", staging: "الفحص النهائي", prod: "التشغيل الفعلي" },
    advanced: "إعدادات متقدمة",
    test: {
      run: "اختبار الاتصال",
      prompt: "إرسال سؤال تجريبي",
      running: "جارٍ الاختبار…",
      ok: "تم الاتصال خلال {{ms}} ملّي ثانية.",
      okModels: "تم الاتصال خلال {{ms}} ملّي ثانية. يوفر {{count}} نماذج: {{list}}",
      reply: "أجاب النموذج: «{{reply}}» ({{ms}} ملّي ثانية).",
      reason: {
        ok: "يعمل.",
        egress_blocked: "محجوب: {{host}} ليس ضمن عناوين الإنترنت المسموح للمنصة بالوصول إليها. اطلب من مسؤول المنصة السماح به (الإدارة › الوصول إلى الإنترنت) أو استخدم نموذجًا داخل شبكتكم.",
        unreachable: "تعذر الوصول إلى العنوان. تحقق من العنوان ومن أن الخدمة تعمل.",
        unauthorised: "رفضت الخدمة مفتاح الواجهة. تحقق من المفتاح أو استبدله.",
        http_error: "أجابت الخدمة بخطأ. تحقق من العنوان.",
        provider_error: "لم يُجب النموذج. تحقق من أن اسم النموذج موجود في هذه الخدمة.",
        not_testable: "يمكن تجربة نماذج المحادثة والبحث (التضمين) فقط هنا.",
        secret_missing: "تعذرت قراءة مفتاح الواجهة المخزن. أدخله مرة أخرى.",
      },
    },
    providers: {
      intro: "يخبر الاتصال المنصة بمكان تشغيل خدمة النماذج: داخل شبكتكم (محلي) أو على الإنترنت (عبر الإنترنت). تحتاج الخدمات عبر الإنترنت إلى مفتاح واجهة وإذن بالوصول إلى الإنترنت.",
      add: "إضافة اتصال",
      name: "الاسم",
      nameHelp: "اسم قصير، مثل «local-gpu» أو «openai».",
      type: "الخدمة",
      where: "مكان التشغيل",
      address: "العنوان",
      addressHelp: "يُملأ تلقائيًا لمعظم الخدمات. غيّره فقط إذا كانت خدمتكم تعمل في مكان آخر.",
      apiKey: "مفتاح الواجهة",
      apiKeyHelp: "يُخزَّن بأمان في الخزنة ولا يُعرض مرة أخرى.",
      keyStored: "مخزَّن ✓ · حُدّث {{at}}",
      keyStoredNoDate: "مخزَّن ✓",
      keyNone: "لا يوجد مفتاح",
      keyNotNeeded: "غير مطلوب",
      replaceKey: "استبدال المفتاح",
      removeKey: "إزالة المفتاح",
      removeKeyConfirm: "إزالة مفتاح الواجهة المخزن؟ ستفشل الطلبات إلى هذه الخدمة حتى إدخال مفتاح جديد.",
      keySaved: "خُزّن مفتاح الواجهة",
      keyRemoved: "أُزيل مفتاح الواجهة",
      keyPlaceholder: "الصق المفتاح هنا",
      secretRef: "مرجع موجود في الخزنة (path#key)",
      secretRefHelp: "فقط للمفاتيح التي وضعها مشغّل مسبقًا في الخزنة.",
      timeout: "التوقف بعد (ثوانٍ)",
      online: "عبر الإنترنت",
      local: "محلي — داخل شبكتكم",
      saved: "حُفظ الاتصال {{name}}",
    },
    models: {
      intro: "النماذج هي محركات الذكاء الاصطناعي التي يستخدمها الوكلاء. ابدأ من الدليل: فهو يملأ تفاصيل النماذج المعروفة.",
      add: "إضافة نموذج",
      fromCatalogue: "البدء من الدليل",
      catalogueHelp: "اختر نموذجًا معروفًا لملء تفاصيله. يمكنك تغيير أي شيء بعد ذلك.",
      catalogueLocal: "يعمل داخل شبكتكم",
      catalogueOnline: "خدمات عبر الإنترنت",
      licence: "الترخيص: {{licence}}",
      priceNote: "تتغير أسعار الخدمات عبر الإنترنت. أدخل السعر الوارد في عقدكم.",
      name: "الاسم الظاهر للوكلاء والمسؤولين",
      connection: "الاتصال",
      serviceName: "اسم النموذج في الخدمة",
      serviceNameHelp: "كما تسميه الخدمة تمامًا، مثل «qwen2.5:7b-instruct».",
      version: "الإصدار الدقيق",
      versionHelp: "يثبّت الأوزان الدقيقة حتى لا تتغير النتائج دون علم.",
      can: "ما يستطيع فعله",
      maxData: "أعلى حساسية بيانات يمكنه رؤيتها",
      contextWindow: "كمية النص التي يقرؤها دفعة واحدة",
      contextValue: "{{n}} وحدة نصية (نحو {{pages}} صفحة)",
      maxAnswer: "أطول إجابة (وحدات نصية)",
      priceIn: "سعر القراءة",
      priceOut: "سعر الكتابة",
      perThousand: "{{currency}} لكل 1,000 وحدة نصية",
      free: "دون رسوم (يعمل على أجهزتكم)",
      noPrice: "لم يُدخل سعر بعد (خدمة عبر الإنترنت: أضف السعر من عقدكم)",
      readyIn: "جاهز لـ{{env}}",
      about: "نبذة",
      dataUpTo: "بيانات حتى {{level}}",
      speed: { fast: "سريع", medium: "أبطأ على الحاسوب المحمول", slow: "يحتاج خادمًا بمعالج رسومي" },
      card: {
        "qwen2-5-0-5b": "نموذج صغير جدًا لفحوص السلامة السريعة والتجارب. لا يصلح للإجابات الفعلية.",
        "qwen2-5-1-5b": "نموذج خاص صغير: سريع، لكن إجاباته بسيطة ولا يُعتمد عليه مع الأنظمة والنماذج.",
        "qwen3-4b": "نموذج خاص أقوى: يستخدم الأنظمة ويملأ النماذج ويكتب إجابات منظمة جيدًا، بالعربية والإنجليزية. أبطأ من النماذج عبر الإنترنت.",
        "qwen2-5-7b": "نموذج خاص جيد للاستخدام اليومي بالعربية والإنجليزية. يحتاج ذاكرة أكبر مما يتوفر في الحاسوب المحمول.",
        "bge-m3": "يجد المقاطع المطابقة في مستنداتك (البحث). لا يكتب إجابات.",
      },
      caps: {
        chat: "المحادثة والإجابات",
        embeddings: "البحث (التضمين)",
        rerank: "إعادة ترتيب نتائج البحث",
        asr: "يفهم الكلام",
        tts: "يتكلّم",
        vision: "قراءة الصور",
        ocr: "قراءة المستندات الممسوحة",
        tools: "استخدام الأدوات",
        json_schema: "تعبئة النماذج (إجابات منظمة)",
        streaming: "عرض الإجابة أثناء كتابتها",
      },
    },
    aliases: {
      title: "خيارات النماذج — الأسماء التي يستخدمها الوكلاء",
      intro: "يطلب الوكلاء خيار نموذج مثل «المحادثة القياسية» وليس نموذجًا بعينه. هنا تحدد النماذج التي تجيب وكيفية توزيع الطلبات. غيّر النماذج هنا دون تغيير أي وكيل.",
      add: "إضافة خيار نموذج",
      name: "الاسم الذي يستخدمه الوكلاء",
      description: "الغرض منه",
      models: "النماذج ونصيب الطلبات",
      share: "نصيب الطلبات",
      quiet: "تجربة بهدوء (0% من الطلبات، مع إمكانية الاختبار)",
      quietTag: "تجربة بهدوء",
      addModel: "إضافة نموذج",
      removeModel: "إزالة النموذج {{n}}",
      total: "المجموع: {{pct}}",
      totalBad: "يجب أن يكون مجموع الأنصبة 100%.",
      even: "توزيع متساوٍ",
      saved: "حُفظ خيار النموذج {{name}}",
    },
    fallbacks: {
      intro: "يُستخدم البديل تلقائيًا عندما لا يجيب نموذج. يجب أن يكون النموذجان معتمدين لتلك المرحلة.",
      add: "إضافة بديل",
      sentence: "إذا لم يكن {{primary}} متاحًا في {{env}}، استخدم {{fallback}}.",
      environment: "أين",
      primary: "إذا لم يكن هذا النموذج متاحًا",
      fallback: "استخدم هذا النموذج بدلًا منه",
      order: "الترتيب (عند وجود عدة بدائل)",
      orderHelp: "تُجرَّب الأرقام الأصغر أولًا.",
      first: "جرّبه أولًا",
      saved: "حُفظ البديل",
    },
    routes: {
      intro: "تعيد قواعد التوجيه توجيه الطلبات في حالات خاصة، مثل إرسال البيانات السرية فقط إلى نماذج داخل شبكتكم. تُفحص القواعد من الأعلى؛ وتُستخدم أول قاعدة مطابقة.",
      add: "إضافة قاعدة توجيه",
      sentence: {
        when: "عندما",
        anyAgent: "يطلب وكيل أي خيار نموذج",
        asks: "يطلب وكيل {{aliases}}",
        task: "للمهمة {{tasks}}",
        tenant: "من {{tenants}}",
        data: "ببيانات {{cls}} أو أكثر حساسية",
        use: "← استخدم {{alias}}",
        keep: "← احتفظ بخيار النموذج",
        pick: { weighted: "ووزّع الطلبات كما هو محدد", cheapest: "واختر الأقل تكلفة", fastest: "واختر الأسرع" },
      },
      edit: {
        aliases: "عندما يطلب وكيل",
        anyChoice: "أي خيار نموذج",
        tasks: "لهذه المهام فقط (اختياري)",
        tenants: "لهذه الجهات فقط (اختياري)",
        minData: "فقط عندما تكون البيانات على الأقل",
        anyData: "أي بيانات",
        routeTo: "استخدم خيار النموذج هذا",
        sameChoice: "احتفظ بما طُلب",
        strategy: "كيفية اختيار النموذج",
        order: "ترتيب الفحص",
        orderHelp: "تُفحص القواعد ذات الرقم الأصغر أولًا.",
      },
      strategy: { weighted: "توزيع الطلبات كما هو محدد", cheapest: "الأقل تكلفة", fastest: "الأسرع" },
      moveUp: "نقل القاعدة {{name}} للأعلى",
      moveDown: "نقل القاعدة {{name}} للأسفل",
      saved: "حُفظت قاعدة التوجيه",
    },
    sim: {
      title: "أي قاعدة تنطبق؟",
      intro: "جرّب طلبًا وشاهد القاعدة المستخدمة والنموذج الذي سيجيب مع الأسباب.",
      alias: "خيار النموذج المطلوب",
      task: "المهمة (اختياري)",
      tenant: "الجهة",
      data: "حساسية البيانات",
      run: "عرض النتيجة",
      ruleUsed: "القاعدة المستخدمة: {{rule}}",
      noRule: "لم تنطبق أي قاعدة — يُستخدم خيار النموذج كما طُلب.",
      becomes: "خيار النموذج المستخدم: {{alias}}",
      answers: "يجيب {{model}} ({{pct}} من الطلبات)",
      backups: "البدائل عند الفشل: {{list}}",
      noBackups: "لا يوجد نموذج بديل.",
      missed: { alias: "يطلب خيار نموذج مختلفًا", task: "مهمة مختلفة", tenant: "جهة مختلفة", classification: "البيانات أقل حساسية" },
      ruleSkipped: "{{rule}} — تم تخطيها: {{why}}",
      targetReason: {
        ok: "يمكنه الإجابة",
        missing_capability: "لا يستطيع تنفيذ هذا النوع من الطلبات",
        not_approved: "غير معتمد لهذه المرحلة بعد",
        classification_too_high: "غير مسموح له برؤية هذه البيانات",
        online_ceiling: "نموذج عبر الإنترنت — البيانات أكثر حساسية من أن تغادر الشبكة",
        egress_blocked: "نموذج عبر الإنترنت — عنوان الإنترنت غير مسموح",
      },
      outcome: {
        ok: "تمت المطابقة",
        unknown_alias: "لا يوجد خيار نموذج بهذا الاسم.",
        no_capable_model: "لا يوجد نموذج ضمن هذا الخيار يستطيع تنفيذ هذا النوع من الطلبات.",
        no_approved_model: "لا يوجد نموذج معتمد ضمن هذا الخيار بعد.",
        no_fitting_model: "لا يوجد نموذج ضمن هذا الخيار مسموح له بتلقي هذه البيانات.",
      },
    },
    cache: {
      intro: "تجعل الإجابات المحفوظة الأسئلة المتكررة أسرع وأقل تكلفة بإعادة استخدام إجابة سابقة.",
      preset: { off: "متوقف", safe: "آمن", aggressive: "مكثف", custom: "مخصص" },
      presetHelp: {
        off: "يذهب كل سؤال إلى نموذج. الأحدث دائمًا والأعلى تكلفة.",
        safe: "إعادة استخدام الإجابة للسؤال المطابق تمامًا فقط لمدة ساعة. موصى به.",
        aggressive: "إعادة استخدام إجابات الأسئلة المتشابهة جدًا أيضًا لمدة تصل إلى يوم. الأقل تكلفة؛ وقد تكون الإجابات أقل حداثة.",
        custom: "إعداداتكم الخاصة (انظر الإعدادات المتقدمة).",
      },
      hitRate: "آخر {{days}} أيام: أُجيب عن {{pct}} من {{requests}} طلبًا من الإجابات المحفوظة.",
      hitRateNone: "لا توجد طلبات في آخر {{days}} أيام.",
      keepFor: "الاحتفاظ بالإجابات المحفوظة لمدة",
      minutes: "دقائق",
      hours: "ساعات",
      neverAbove: "عدم حفظ الإجابات للبيانات الأعلى من",
      similarity: "مدى التشابه المطلوب بين الأسئلة (الأعلى = أدق)",
      embedChoice: "خيار النموذج المستخدم لمقارنة الأسئلة",
      needsEmbed: "يتطلب الوضع المكثف خيار نموذج بحث (تضمين) محليًا.",
      saved: "حُفظت إعدادات الإجابات المحفوظة",
    },
    limits: {
      title: "نُقلت حدود الإنفاق",
      body: "تُضبط حدود الإنفاق الآن في مكان واحد: الإدارة › حدود الإنفاق. يضبط فريق المالية ومسؤولو الجهة هناك الحدود لكل جهة أو فريق أو مشروع أو وكيل.",
      open: "فتح حدود الإنفاق",
      legacyTitle: "حدود سابقة ضُبطت في هذه الصفحة",
      legacyBody: "ضُبطت هذه الحدود هنا قبل نقل حدود الإنفاق، ولم تعد تُستخدم: تُطبَّق بدلًا منها الحدود في الإدارة › حدود الإنفاق. يمكنك إزالتها.",
      legacyActive: "ما زالت هذه المنصة تفحص هذه الحدود السابقة، لأن الإنفاق لا يُحتسب مركزيًا هنا.",
      tokens: "{{n}} وحدة نصية (نحو {{words}} كلمة)",
      who: "يشمل",
      amount: "الحد",
      remove: "إزالة الحد {{name}}",
      removeConfirm: "إزالة هذا الحد السابق؟",
      removed: "أُزيل الحد السابق",
    },
  },
};
